import { z } from 'zod';
import {
  AiBillingUsageSchema,
  AiToolBillingModeSchema,
  type AiBillingBreakdown,
} from './ai-billing';

export const AI_CHAT_MESSAGE_MAX_CHARS = 32_000;
export const AI_CHAT_RESPONSE_MAX_CHARS = 128_000;

export const AI_CHAT_ATTACHMENT_KINDS = [
  'image',
  'pdf',
  'office',
  'text',
  'code',
  'audio',
  'video',
] as const;
export const AiChatAttachmentKindSchema = z.enum(AI_CHAT_ATTACHMENT_KINDS);
export type AiChatAttachmentKind = z.infer<typeof AiChatAttachmentKindSchema>;

export const AI_CHAT_ATTACHMENT_MAX_COUNT = 5;
export const AI_CHAT_ATTACHMENT_MAX_TOTAL_BYTES = 50 * 1024 * 1024;
export const AI_CHAT_ATTACHMENT_MAX_VIDEO_DURATION_MS = 5 * 60 * 1_000;
export const AI_CHAT_ATTACHMENT_MAX_BYTES = {
  image: 10 * 1024 * 1024,
  pdf: 20 * 1024 * 1024,
  office: 20 * 1024 * 1024,
  text: 5 * 1024 * 1024,
  code: 5 * 1024 * 1024,
  audio: 50 * 1024 * 1024,
  video: 50 * 1024 * 1024,
} as const satisfies Record<AiChatAttachmentKind, number>;

export const AiChatAttachmentIdSchema = z
  .string()
  .trim()
  .min(16)
  .max(128)
  .regex(/^[a-zA-Z0-9_-]+$/);

export const AiChatAttachmentCreateRequestSchema = z
  .object({
    filename: z.string().trim().min(1).max(255),
    mime_type: z.string().trim().toLowerCase().min(1).max(160),
    size_bytes: z.number().int().min(1).max(AI_CHAT_ATTACHMENT_MAX_TOTAL_BYTES),
    width: z.number().int().min(1).max(100_000).optional(),
    height: z.number().int().min(1).max(100_000).optional(),
    duration_ms: z.number().int().min(1).max(86_400_000).optional(),
  })
  .strict()
  .refine((value) => (value.width === undefined) === (value.height === undefined), {
    message: 'attachment width and height must be provided together',
    path: ['width'],
  });
export type AiChatAttachmentCreateRequest = z.infer<typeof AiChatAttachmentCreateRequestSchema>;

export const AiChatAttachmentDescriptorSchema = z
  .object({
    id: AiChatAttachmentIdSchema,
    kind: AiChatAttachmentKindSchema,
    filename: z.string().min(1).max(255),
    mime_type: z.string().min(1).max(160),
    size_bytes: z.number().int().min(1).max(AI_CHAT_ATTACHMENT_MAX_TOTAL_BYTES),
    width: z.number().int().min(1).max(100_000).optional(),
    height: z.number().int().min(1).max(100_000).optional(),
    duration_ms: z.number().int().min(1).max(86_400_000).optional(),
  })
  .strict();
export type AiChatAttachmentDescriptor = z.infer<typeof AiChatAttachmentDescriptorSchema>;

export const AiChatAttachmentStatusSchema = z.enum(['pending', 'uploaded', 'bound']);
export type AiChatAttachmentStatus = z.infer<typeof AiChatAttachmentStatusSchema>;

export const AiChatAttachmentRecordSchema = AiChatAttachmentDescriptorSchema.extend({
  status: AiChatAttachmentStatusSchema,
  expires_at: z.string().trim().min(1).max(64).nullable(),
}).strict();
export type AiChatAttachmentRecord = z.infer<typeof AiChatAttachmentRecordSchema>;

export const AiChatAttachmentCreateResponseSchema = z
  .object({
    attachment: AiChatAttachmentRecordSchema,
    upload: z
      .object({
        method: z.literal('PUT'),
        path: z.string().min(1).max(512),
        content_type: z.string().min(1).max(160),
        content_length: z.number().int().min(1).max(AI_CHAT_ATTACHMENT_MAX_TOTAL_BYTES),
      })
      .strict(),
  })
  .strict();
export type AiChatAttachmentCreateResponse = z.infer<typeof AiChatAttachmentCreateResponseSchema>;

export const AiChatAttachmentUploadResponseSchema = z
  .object({ attachment: AiChatAttachmentRecordSchema })
  .strict();
export type AiChatAttachmentUploadResponse = z.infer<typeof AiChatAttachmentUploadResponseSchema>;

export const AiChatAttachmentDeleteResponseSchema = z
  .object({
    attachment_id: AiChatAttachmentIdSchema,
    status: z.literal('deleted'),
  })
  .strict();
export type AiChatAttachmentDeleteResponse = z.infer<typeof AiChatAttachmentDeleteResponseSchema>;

export interface AiChatAttachmentKindCapability {
  supported: boolean;
  max_bytes: number;
  max_duration_ms: number | null;
}

export interface AiChatAttachmentModelCapabilities {
  max_count: number;
  max_total_bytes: number;
  kinds: Record<AiChatAttachmentKind, AiChatAttachmentKindCapability>;
}

export const AiChatMessageSchema = z
  .object({
    role: z.enum(['user', 'assistant']),
    content: z.string().trim().min(1).max(AI_CHAT_MESSAGE_MAX_CHARS),
    attachment_ids: z
      .array(AiChatAttachmentIdSchema)
      .max(AI_CHAT_ATTACHMENT_MAX_COUNT)
      .refine((ids) => new Set(ids).size === ids.length, {
        message: 'attachment ids must be unique',
      })
      .optional(),
  })
  .strict()
  .superRefine((message, context) => {
    if (message.role !== 'user' && message.attachment_ids && message.attachment_ids.length > 0) {
      context.addIssue({
        code: 'custom',
        path: ['attachment_ids'],
        message: 'only user messages may include attachments',
      });
    }
  });

/** Public request contract for the queue-backed Chat generation endpoint. */
export const AiChatCompletionRequestSchema = z
  .object({
    client_message_id: z.string().trim().min(1).max(128),
    conversation_id: z.string().trim().min(1).max(128).optional(),
    messages: z
      .array(AiChatMessageSchema)
      .min(1)
      .max(64)
      .refine((messages) => messages.some((message) => message.role === 'user'), {
        message: 'at least one user message is required',
      }),
    model_id: z.string().trim().min(1).max(160).optional(),
    max_output_tokens: z.number().int().min(1).max(4_096).default(1_024),
  })
  .strict()
  .superRefine((request, context) => {
    let submittedUserIndex = -1;
    for (let index = request.messages.length - 1; index >= 0; index -= 1) {
      if (request.messages[index]?.role === 'user') {
        submittedUserIndex = index;
        break;
      }
    }
    request.messages.forEach((message, index) => {
      if (
        index !== submittedUserIndex &&
        message.attachment_ids &&
        message.attachment_ids.length > 0
      ) {
        context.addIssue({
          code: 'custom',
          path: ['messages', index, 'attachment_ids'],
          message: 'only the submitted user message may include attachment ids',
        });
      }
    });
  });

export type AiChatCompletionRequest = z.infer<typeof AiChatCompletionRequestSchema>;

export const AiChatQuoteRequestSchema = z
  .object({
    conversation_id: z.string().trim().min(1).max(128).optional(),
    draft: z.string().trim().min(1).max(AI_CHAT_MESSAGE_MAX_CHARS),
    model_id: z.string().trim().min(1).max(160),
    max_output_tokens: z.number().int().min(1).max(4_096).default(1_024),
    attachment_ids: z
      .array(AiChatAttachmentIdSchema)
      .max(AI_CHAT_ATTACHMENT_MAX_COUNT)
      .refine((ids) => new Set(ids).size === ids.length, {
        message: 'attachment ids must be unique',
      })
      .optional(),
  })
  .strict();

export type AiChatQuoteRequest = z.input<typeof AiChatQuoteRequestSchema>;

export const AiChatQuoteBlockReasonSchema = z.enum([
  'chat.insufficient_points',
  'chat.model_not_allowed',
  'chat.model_unavailable',
  'chat.attachment_model_unsupported',
  'chat.quote_config_unavailable',
]);
export type AiChatQuoteBlockReason = z.infer<typeof AiChatQuoteBlockReasonSchema>;

const AiChatSafeIntegerSchema = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);

export const AiChatQuoteBackendCostSchema = z
  .object({
    currency: z.literal('USD'),
    input_micro_usd: AiChatSafeIntegerSchema,
    output_micro_usd: AiChatSafeIntegerSchema,
    total_micro_usd: AiChatSafeIntegerSchema,
  })
  .strict();
export type AiChatQuoteBackendCost = z.infer<typeof AiChatQuoteBackendCostSchema>;

export const AiChatQuoteResponseSchema = z
  .object({
    model_id: z.string().trim().min(1).max(160),
    estimated_input_tokens: AiChatSafeIntegerSchema,
    max_output_tokens: z.number().int().min(1).max(4_096),
    estimated_points: AiChatSafeIntegerSchema.nullable(),
    reserved_points: AiChatSafeIntegerSchema.nullable(),
    available_points: AiChatSafeIntegerSchema.nullable(),
    points_unlimited: z.boolean(),
    can_send: z.boolean(),
    block_reason: AiChatQuoteBlockReasonSchema.nullable(),
    backend_cost: AiChatQuoteBackendCostSchema.nullable(),
  })
  .strict();
export type AiChatQuoteResponse = z.infer<typeof AiChatQuoteResponseSchema>;

/** Chat 终态计费合同：完整或可验证的部分 usage 结算，其余路径退款。 */
export const AiChatBillingOutcomeSchema = z.enum([
  'completed',
  'partial_usage',
  'provider_failed',
  'provider_timeout',
  'first_token_timeout',
  'total_timeout',
  'client_disconnected',
  'persistence_failed',
]);

export type AiChatBillingOutcome = z.infer<typeof AiChatBillingOutcomeSchema>;

export const AiChatBillingFinalizationSchema = z
  .object({
    reservation_id: z.string().trim().min(1).max(80),
    outcome: AiChatBillingOutcomeSchema,
    final_model_id: z.string().trim().min(1).max(160).nullable().optional(),
    reason: z.string().trim().max(512).nullable().optional(),
  })
  .strict();

export type AiChatBillingFinalization = z.infer<typeof AiChatBillingFinalizationSchema>;

export function aiChatBillingSettlementAction(outcome: AiChatBillingOutcome): 'settle' | 'refund' {
  return outcome === 'completed' || outcome === 'partial_usage' ? 'settle' : 'refund';
}

export const AiChatCompletionUsageSchema = z
  .object({
    prompt_tokens: z.number().int().nonnegative().nullable(),
    completion_tokens: z.number().int().nonnegative().nullable(),
    cache_read_tokens: z.number().int().nonnegative().nullable(),
    cache_write_tokens: z.number().int().nonnegative().nullable(),
    reasoning_tokens: z.number().int().nonnegative().nullable(),
    total_tokens: z.number().int().nonnegative().nullable(),
    image_input_units: z.number().int().nonnegative().optional(),
    image_output_units: z.number().int().nonnegative().optional(),
    audio_input_units: z.number().int().nonnegative().optional(),
    audio_output_units: z.number().int().nonnegative().optional(),
  })
  .strict();

export type AiChatCompletionUsage = z.infer<typeof AiChatCompletionUsageSchema>;

const AiChatBillingBreakdownSchema = z
  .object({
    mode: z.enum(['fixed', 'metered', 'hybrid']),
    attempt_count: AiChatSafeIntegerSchema,
    model_id: z.string().trim().min(1).max(160).nullable(),
    model_multiplier_milli: AiChatSafeIntegerSchema.nullable(),
    usage: AiBillingUsageSchema,
    components_micro_points: z
      .object({
        base: AiChatSafeIntegerSchema,
        input_tokens: AiChatSafeIntegerSchema,
        output_tokens: AiChatSafeIntegerSchema,
        cached_tokens: AiChatSafeIntegerSchema,
        reasoning_tokens: AiChatSafeIntegerSchema,
        image_input_units: AiChatSafeIntegerSchema,
        image_output_units: AiChatSafeIntegerSchema,
        audio_input_units: AiChatSafeIntegerSchema,
        audio_output_units: AiChatSafeIntegerSchema,
        tool_calls: AiChatSafeIntegerSchema,
      })
      .strict(),
    tool_details: z.array(
      z
        .object({
          tool_type: z.string().trim().min(1).max(120),
          tool_name: z.string().trim().min(1).max(120),
          mode: AiToolBillingModeSchema,
          skill_id: z.string().trim().min(1).max(128).nullable(),
          attempt_count: AiChatSafeIntegerSchema,
          failed_count: AiChatSafeIntegerSchema,
          micro_points: AiChatSafeIntegerSchema,
        })
        .strict(),
    ),
    total_points: AiChatSafeIntegerSchema,
    reserved_points: AiChatSafeIntegerSchema.nullable(),
    settled_points: AiChatSafeIntegerSchema.nullable(),
    released_points: AiChatSafeIntegerSchema.nullable(),
  })
  .strict() satisfies z.ZodType<AiBillingBreakdown>;

export const AiChatSettlementStatusSchema = z.enum([
  'reserved',
  'pending',
  'settled',
  'refunded',
  'failed',
  'cancelled',
]);
export type AiChatSettlementStatus = z.infer<typeof AiChatSettlementStatusSchema>;

export const AiChatCompletionResponseSchema = z
  .object({
    client_message_id: z.string().trim().min(1).max(128),
    conversation_id: z.string().trim().min(1).max(128).nullable(),
    user_message_id: z.string().trim().min(1).max(128).nullable(),
    assistant_message_id: z.string().trim().min(1).max(128).nullable(),
    status: z.enum(['succeeded', 'processing', 'failed']),
    reused: z.boolean(),
    reservation_id: z.string().trim().min(1).max(80).nullable(),
    model_id: z.string().trim().min(1).max(160).nullable(),
    assistant: z
      .object({
        role: z.literal('assistant'),
        content: z.string().max(AI_CHAT_RESPONSE_MAX_CHARS),
      })
      .strict()
      .nullable(),
    text: z.string().max(AI_CHAT_RESPONSE_MAX_CHARS).nullable(),
    usage: AiChatCompletionUsageSchema.nullable(),
    settlement_status: AiChatSettlementStatusSchema.nullable(),
    settled_points: AiChatSafeIntegerSchema.nullable(),
    settlement_error: z.string().max(1_000).nullable(),
    billing: AiChatBillingBreakdownSchema.nullable(),
    error: z
      .object({
        code: z.string().trim().min(1).max(120),
        message: z.string().max(1_000).optional(),
      })
      .strict()
      .optional(),
  })
  .strict();

export type AiChatCompletionResponse = z.infer<typeof AiChatCompletionResponseSchema>;

export const AiChatGenerationStatusSchema = z.enum(['queued', 'processing', 'succeeded', 'failed']);
export type AiChatGenerationStatus = z.infer<typeof AiChatGenerationStatusSchema>;

/**
 * `POST /v1/ai/chat/messages` 的异步迁移合同。请求只完成持久化、冻结、
 * 预留和入队；完整结果由 result_url 读取，增量由 events_url 订阅。
 */
export const AiChatGenerationAcceptedResponseSchema = z
  .object({
    generation_id: z.string().trim().min(1).max(128),
    client_message_id: z.string().trim().min(1).max(128),
    conversation_id: z.string().trim().min(1).max(128),
    user_message_id: z.string().trim().min(1).max(128),
    assistant_message_id: z.string().trim().min(1).max(128),
    status: AiChatGenerationStatusSchema,
    reused: z.boolean(),
    reservation_id: z.string().trim().min(1).max(80),
    model_id: z.string().trim().min(1).max(160),
    events_url: z.string().trim().min(1).max(512),
    result_url: z.string().trim().min(1).max(512),
    cursor: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
  })
  .strict();

export type AiChatGenerationAcceptedResponse = z.infer<
  typeof AiChatGenerationAcceptedResponseSchema
>;

/** 服务端单次 SSE 连接有界关闭，客户端随后从最后 cursor 继续重放。 */
export const AI_CHAT_STREAM_MAX_EVENTS = 50_000;
export const AI_CHAT_STREAM_CONNECTION_MAX_EVENTS = 1_000;
export const AI_CHAT_STREAM_CONNECTION_MAX_BYTES = 4 * 1024 * 1024;
export const AI_CHAT_STREAM_CLIENT_MAX_BYTES = 8 * 1024 * 1024;

export const AiChatCursorConflictDetailsSchema = z
  .object({
    current_cursor: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
  })
  .strict();
export type AiChatCursorConflictDetails = z.infer<typeof AiChatCursorConflictDetailsSchema>;

const AiChatStreamEventBaseSchema = z.object({
  generation_id: z.string().trim().min(1).max(128),
  cursor: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
  created_at: z.string().trim().min(1).max(64),
});

export const AiChatStreamDeltaEventSchema = AiChatStreamEventBaseSchema.extend({
  type: z.literal('delta'),
  text: z.string().min(1).max(65_536),
}).strict();

export const AiChatStreamUsageEventSchema = AiChatStreamEventBaseSchema.extend({
  type: z.literal('usage'),
  usage: AiChatCompletionUsageSchema,
}).strict();

export const AiChatStreamFinalEventSchema = AiChatStreamEventBaseSchema.extend({
  type: z.literal('final'),
  status: z.literal('succeeded'),
}).strict();

export const AiChatStreamErrorEventSchema = AiChatStreamEventBaseSchema.extend({
  type: z.literal('error'),
  status: z.literal('failed'),
  error: z
    .object({
      code: z.string().trim().min(1).max(120),
      message: z.string().trim().min(1).max(1_000).optional(),
    })
    .strict(),
}).strict();

export const AiChatStreamEventSchema = z.discriminatedUnion('type', [
  AiChatStreamDeltaEventSchema,
  AiChatStreamUsageEventSchema,
  AiChatStreamFinalEventSchema,
  AiChatStreamErrorEventSchema,
]);
export type AiChatStreamEvent = z.infer<typeof AiChatStreamEventSchema>;

export const AiChatQueueMessageSchema = z
  .object({
    schema_version: z.literal(1),
    generation_id: z.string().trim().min(1).max(128),
    tenant_id: z.string().trim().min(1).max(128),
    user_id: z.string().trim().min(1).max(128),
  })
  .strict();
export type AiChatQueueMessage = z.infer<typeof AiChatQueueMessageSchema>;

const AiChatPublicIdSchema = z.string().trim().min(1).max(128);
const AiChatTimestampSchema = z.string().trim().min(1).max(64);

export const AiChatConversationListQuerySchema = z
  .object({
    cursor: z.string().trim().min(1).max(512).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(30),
  })
  .strict();
export type AiChatConversationListQuery = z.infer<typeof AiChatConversationListQuerySchema>;

export const AiChatMessageListQuerySchema = z
  .object({
    cursor: z.string().trim().min(1).max(512).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
  })
  .strict();
export type AiChatMessageListQuery = z.infer<typeof AiChatMessageListQuerySchema>;

/** `/v1/ai/chat/conversations` 公开会话合同。 */
export const AiChatConversationSchema = z
  .object({
    id: AiChatPublicIdSchema,
    title: z.string().min(1).max(120),
    default_model_id: z.string().trim().min(1).max(160).nullable(),
    last_activity_at: AiChatTimestampSchema,
    created_at: AiChatTimestampSchema,
    updated_at: AiChatTimestampSchema,
  })
  .strict();
export type AiChatConversation = z.infer<typeof AiChatConversationSchema>;

export const AiChatConversationListResponseSchema = z
  .object({
    items: z.array(AiChatConversationSchema),
    next_cursor: z.string().min(1).nullable(),
  })
  .strict();
export type AiChatConversationListResponse = z.infer<typeof AiChatConversationListResponseSchema>;

export const AiChatStoredMessageStatusSchema = z.enum(['processing', 'succeeded', 'failed']);
export type AiChatStoredMessageStatus = z.infer<typeof AiChatStoredMessageStatusSchema>;

/**
 * 历史消息只公开产品模型、状态、用量、附件描述与 reservation 结算事实；
 * runtime provider、Gateway、对象 key 和内部执行快照都留在服务端。
 */
export const AiChatStoredMessageSchema = z
  .object({
    id: AiChatPublicIdSchema,
    conversation_id: AiChatPublicIdSchema,
    client_message_id: z.string().trim().min(1).max(128).nullable(),
    role: z.enum(['user', 'assistant']),
    content: z.string().max(AI_CHAT_RESPONSE_MAX_CHARS).nullable(),
    attachments: z.array(AiChatAttachmentDescriptorSchema).max(AI_CHAT_ATTACHMENT_MAX_COUNT),
    status: AiChatStoredMessageStatusSchema,
    requested_model_id: z.string().trim().min(1).max(160).nullable(),
    model_id: z.string().trim().min(1).max(160).nullable(),
    reservation_id: z.string().trim().min(1).max(80).nullable(),
    settlement_status: AiChatSettlementStatusSchema.nullable().default(null),
    settled_points: AiChatSafeIntegerSchema.nullable().default(null),
    billing: AiChatBillingBreakdownSchema.nullable().default(null),
    usage: AiChatCompletionUsageSchema.nullable(),
    error: z
      .object({
        code: z.string().trim().min(1).max(120),
        message: z.string().max(1_000).nullable(),
      })
      .strict()
      .nullable(),
    created_at: AiChatTimestampSchema,
    updated_at: AiChatTimestampSchema,
    completed_at: AiChatTimestampSchema.nullable(),
  })
  .strict()
  .superRefine((message, context) => {
    if (
      message.role === 'user' &&
      message.content !== null &&
      message.content.length > AI_CHAT_MESSAGE_MAX_CHARS
    ) {
      context.addIssue({
        code: 'custom',
        path: ['content'],
        message: `user message content must be at most ${AI_CHAT_MESSAGE_MAX_CHARS} characters`,
      });
    }
  });
export type AiChatStoredMessage = z.infer<typeof AiChatStoredMessageSchema>;

export const AiChatMessageListResponseSchema = z
  .object({
    conversation: AiChatConversationSchema,
    items: z.array(AiChatStoredMessageSchema),
    next_cursor: z.string().min(1).nullable(),
  })
  .strict();
export type AiChatMessageListResponse = z.infer<typeof AiChatMessageListResponseSchema>;

export const AiChatConversationRenameRequestSchema = z
  .object({ title: z.string().trim().min(1).max(120) })
  .strict();
export type AiChatConversationRenameRequest = z.infer<typeof AiChatConversationRenameRequestSchema>;

export const AiChatConversationDeleteResponseSchema = z
  .object({
    conversation_id: AiChatPublicIdSchema,
    status: z.literal('deleted'),
  })
  .strict();
export type AiChatConversationDeleteResponse = z.infer<
  typeof AiChatConversationDeleteResponseSchema
>;
